import {
    ClickAwayListener,
    Dialog,
    Grid,
    IconButton,
    Popper,
    PopperPlacementType,
    SxProps,
    Theme,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AcxMainTextField from "components/UI/AcxMainTextField";
import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { HierarchyService } from "services/HierarchyService";
import { BranchDataProps } from "../AcxRecursiveTree/AcxRecursiveTreeBranch";
import RecursiveTreeStore from "../AcxRecursiveTree/Stores/AcxRecursiveTreeStore";
import AcxSelectMulti from "../Select/BaseSelectComponents/AcxSelectMulti";
import HierarchySelectorBase from "./HierarchySelectorBase";
import { observer } from "mobx-react";
import DownChevronSvg from "../Select/BaseSelectComponents/DownChevronSvg";
import { useStore } from "utils/useStore";
import { useLocation } from "react-router";
import ClassifierBuilderV2ReclassifyDialogStore from "components/Classifiers/ClassifierBuilderV2/Stores/ClassifierBuilderV2ReclassifyDialogStore";
import { useTheme } from "@mui/material";

export interface IAcxHierarchyComponentProps
    extends IAcxHierarchySelectorProps {
    treeStore: RecursiveTreeStore;
    onApplyClick?: () => void;
}

interface IAcxHierarchySelectorProps {
    userId: string;
    orgId: string;
    displayType: UiDisplayType;
    setHierarchyIds: (selectedBranchIds: string[]) => void;
    onSaveUpdateWithHierarchies: (
        selectedBranchIds?: string[],
        treeStore?: RecursiveTreeStore,
    ) => void;
    initSelectedHierarchyIds?: string[];
    initSelectedHierarchyLabels?: string[];
    placeholder?: string;
    hideTopContent?: boolean;
    hideBottomContent?: boolean;
    removeMinHeight?: boolean;
    removeMinWidth?: boolean;
    fullHeight?: boolean;
    width?: string;
    popperWidth?: string;
    applyBtnText?: string;
    singleSelect?: boolean;
    fixedPopper?: boolean;
    popperOpen?: boolean;
    popperPlacement?: "bottom" | "top" | PopperPlacementType;
    anchorEl?: any;
    handlePopperClose?: () => void;
    popToLeft?: boolean;
    labelText?: string;
    customZIndex?: number;
    treeStoreGetter?: (treeStore: RecursiveTreeStore) => void;
    storeFilterCategories?: (categories) => void;
    storeFilterSubategories?: (subcategories) => void;
    initCategories?: { categories: string[]; subcategories: string[] };
    /**
     * Only supported by displayType = "input" as of now (our only use case).
     */
    viewOnly?: boolean;
    /**
     * Restricts hierarchy options to this explicit ID set when provided.
     */
    availableHierarchyIds?: string[];
    /**
     * Hides hierarchy category and subcategory selectors.
     */
    hideCategorySelectors?: boolean;
    maxSelectedHierarchyCount?: number;
    selectionLimitMessage?: string;
}

type UiDisplayType =
    | "baseOnly" // analyticx > eddy > filter & motfDefintionForm
    | "input" // conversations > search filters > input
    | "inputDialog" // analyticx > report > builder reclassify > input with confirm dialog
    | "popperOnly"; // dashboard & widget editor

function normalizeHierarchyRestrictionIds(
    availableHierarchyIds?: string[],
): string[] {
    const normalizedIds = (availableHierarchyIds ?? [])
        .map((hierarchyId) => `${hierarchyId ?? ""}`.trim())
        .filter((hierarchyId) => hierarchyId.length > 0);

    return Array.from(new Set(normalizedIds)).sort((left, right) =>
        left.localeCompare(right),
    );
}

function getSelectableHierarchyChildren(
    hierarchies: BranchDataProps[],
    hasAvailableHierarchyRestriction: boolean,
    availableHierarchyIdSet: ReadonlySet<string>,
): BranchDataProps[] {
    return hierarchies.filter(
        (hierarchy) =>
            hierarchy.hasPerms !== false &&
            (!hasAvailableHierarchyRestriction ||
                availableHierarchyIdSet.has(`${hierarchy.id}`)),
    );
}

export function flattenHierarchyBranchesForSelection(
    hierarchies: BranchDataProps[],
): BranchDataProps[] {
    const flattenedBranches: BranchDataProps[] = [];
    const visitedBranchIds = new Set<string>();

    const appendBranch = (branch: BranchDataProps) => {
        const branchId = `${branch.id}`;

        if (visitedBranchIds.has(branchId)) return;

        visitedBranchIds.add(branchId);
        flattenedBranches.push(branch);
        branch.children?.forEach(appendBranch);
    };

    hierarchies.forEach(appendBranch);

    return flattenedBranches;
}

export function orderHierarchyBranchesForSelection(
    hierarchies: BranchDataProps[],
    parentBranchId?: string,
): BranchDataProps[] {
    const branchById = new Map(
        hierarchies.map((hierarchy) => [`${hierarchy.id}`, hierarchy]),
    );
    const childrenByParentId = new Map<string, BranchDataProps[]>();

    hierarchies.forEach((hierarchy) => {
        const parentMemberId = hierarchy.parentMemberId
            ? `${hierarchy.parentMemberId}`
            : "";
        const siblingBranches = childrenByParentId.get(parentMemberId) ?? [];

        siblingBranches.push(hierarchy);
        childrenByParentId.set(parentMemberId, siblingBranches);
    });

    const orderedBranches: BranchDataProps[] = [];
    const visitedBranchIds = new Set<string>();

    const appendBranch = (branch: BranchDataProps) => {
        const branchId = `${branch.id}`;

        if (visitedBranchIds.has(branchId)) return;

        visitedBranchIds.add(branchId);
        orderedBranches.push(branch);
        childrenByParentId.get(branchId)?.forEach(appendBranch);
    };

    if (parentBranchId) {
        childrenByParentId.get(parentBranchId)?.forEach(appendBranch);
    }

    hierarchies
        .filter((hierarchy) => {
            if (visitedBranchIds.has(`${hierarchy.id}`)) return false;
            if (parentBranchId) return false;

            return (
                !hierarchy.parentMemberId ||
                !branchById.has(`${hierarchy.parentMemberId}`)
            );
        })
        .forEach(appendBranch);

    hierarchies.forEach(appendBranch);

    return orderedBranches;
}

/**
 * Keep allowed descendants and their ancestor chain so restricted child selections
 * remain navigable in the tree while still hiding disallowed branches.
 */
function filterHierarchyChildrenForDisplay(
    hierarchies: BranchDataProps[],
    hasAvailableHierarchyRestriction: boolean,
    availableHierarchyIdSet: ReadonlySet<string>,
): BranchDataProps[] {
    if (!hasAvailableHierarchyRestriction) return hierarchies;
    if (hierarchies.length === 0) return [];

    const allowedOrAncestorIds = new Set(availableHierarchyIdSet);
    let hasChanges = true;

    while (hasChanges) {
        hasChanges = false;

        for (const hierarchy of hierarchies) {
            const hierarchyId = `${hierarchy.id}`;
            const parentMemberId = hierarchy.parentMemberId
                ? `${hierarchy.parentMemberId}`
                : undefined;

            if (!parentMemberId) continue;
            if (!allowedOrAncestorIds.has(hierarchyId)) continue;
            if (allowedOrAncestorIds.has(parentMemberId)) continue;

            allowedOrAncestorIds.add(parentMemberId);
            hasChanges = true;
        }
    }

    return hierarchies.filter((hierarchy) =>
        allowedOrAncestorIds.has(`${hierarchy.id}`),
    );
}

const AcxHierarchySelector: React.FC<IAcxHierarchySelectorProps> = observer(
    ({
        treeStoreGetter,
        popperWidth = "500px",
        ...props
    }: IAcxHierarchySelectorProps) => {
        const [anchorEl, setAnchorEl] = useState<
            HTMLElement | (() => HTMLElement) | null | undefined
        >(null);
        const location = useLocation();
        const theme = useTheme();

        const [popperOpen, setPopperOpen] = useState<boolean>(false);
        const [dialogOpen, setDialogOpen] = useState<boolean>(false);
        const popperId = popperOpen ? "hierarchies-popper" : undefined;
        const inputRef = useRef(null);

        const hierService = useMemo(() => new HierarchyService(), []);

        const rbcReclassifyStore = useStore(
            ClassifierBuilderV2ReclassifyDialogStore,
        );
        const normalizedAvailableHierarchyIdsKey = useMemo(
            () =>
                normalizeHierarchyRestrictionIds(
                    props.availableHierarchyIds,
                ).join("\u0000"),
            [props.availableHierarchyIds],
        );
        const availableHierarchyIds = useMemo(
            () =>
                normalizedAvailableHierarchyIdsKey.length > 0
                    ? normalizedAvailableHierarchyIdsKey.split("\u0000")
                    : [],
            [normalizedAvailableHierarchyIdsKey],
        );
        const hasAvailableHierarchyRestriction =
            availableHierarchyIds.length > 0;
        const availableHierarchyIdSet = useMemo(
            () => new Set(availableHierarchyIds.map((id) => `${id}`)),
            [availableHierarchyIds],
        );

        const treeStore = useMemo(() => {
            const newTreeStore = new RecursiveTreeStore({
                getBranch: async (orgId, osmId) => {
                    const res =
                        await await hierService.getAllChildrenForHierarchyAsync(
                            osmId,
                        );
                    const filteredHierarchyChildren =
                        filterHierarchyChildrenForDisplay(
                            res.childrenHierarchies,
                            hasAvailableHierarchyRestriction,
                            availableHierarchyIdSet,
                        );
                    return {
                        children: filteredHierarchyChildren,
                        rowCount: getSelectableHierarchyChildren(
                            res.childrenHierarchies,
                            hasAvailableHierarchyRestriction,
                            availableHierarchyIdSet,
                        ).length,
                    };
                },
                updateBranch: (() => {}) as any,
            });
            newTreeStore.displayChildSelection = !props.singleSelect;
            newTreeStore.enableCheckboxSelection = true;

            newTreeStore.singleSelect = !!props.singleSelect;
            newTreeStore.setBranchContent((currentBranch: BranchDataProps) => {
                return (
                    <Grid
                        style={{
                            alignSelf: "center",
                            paddingTop: "1px",
                        }}
                        item
                        container
                    >
                        <Tooltip title={currentBranch.name}>
                            <Typography noWrap>{currentBranch.name}</Typography>
                        </Tooltip>
                    </Grid>
                );
            });

            const getOrderedSelectableChildren = async (
                parentBranchId: string,
            ) => {
                const res =
                    await hierService.getAllChildrenForHierarchyAsync(
                        parentBranchId,
                    );
                const selectableChildren = getSelectableHierarchyChildren(
                    res.childrenHierarchies,
                    hasAvailableHierarchyRestriction,
                    availableHierarchyIdSet,
                );

                return orderHierarchyBranchesForSelection(
                    selectableChildren,
                    parentBranchId,
                );
            };

            newTreeStore.getSelectableChildIds = async (
                parentBranchId: string,
            ) => {
                const orderedSelectableChildren =
                    await getOrderedSelectableChildren(parentBranchId);

                newTreeStore.setSelectableChildrenForParentBranch(
                    parentBranchId,
                    orderedSelectableChildren.map((branch) => ({
                        id: branch.id,
                        label: branch.name,
                    })),
                );

                return orderedSelectableChildren.map((branch) => branch.id);
            };

            // parentBranchId/selected branch as osmId
            newTreeStore.onSelectAllChildren = (
                parentBranchId: string,
                isChecked: boolean,
            ) => {
                const cachedChildren =
                    treeStore.selectableChildrenByParentBranchId[
                        parentBranchId
                    ];
                const selectableChildrenPromise = cachedChildren?.length
                    ? Promise.resolve(cachedChildren)
                    : getOrderedSelectableChildren(parentBranchId).then(
                          (branches) => {
                              const children = branches.map((branch) => ({
                                  id: branch.id,
                                  label: branch.name,
                              }));

                              treeStore.setSelectableChildrenForParentBranch(
                                  parentBranchId,
                                  children,
                              );

                              return children;
                          },
                      );

                selectableChildrenPromise
                    .then((res) => {
                        let idArr: string[] = [];
                        let labelArr: string[] = [];
                        res.forEach((child) => {
                            idArr.push(child.id);
                            labelArr.push(child.label);
                        });
                        isChecked
                            ? treeStore.removeMultipleSelectedBranches(
                                  idArr,
                                  labelArr,
                              )
                            : (() => {
                                  treeStore.addMultipleSelectedBranches(
                                      idArr,
                                      labelArr,
                                  );
                              })();
                    });
            };

            newTreeStore.getRecursiveChildCount = async (
                parentBranchId: string,
            ) => {
                const res = await hierService.getAllChildrenForHierarchyAsync(
                    parentBranchId,
                );
                return getSelectableHierarchyChildren(
                    res.childrenHierarchies,
                    hasAvailableHierarchyRestriction,
                    availableHierarchyIdSet,
                ).length;
            };

            return newTreeStore;
        }, [
            props.singleSelect,
            hierService,
            hasAvailableHierarchyRestriction,
            availableHierarchyIdSet,
        ]);

        useEffect(() => {
            treeStoreGetter?.(treeStore);
        }, [treeStore, treeStoreGetter]);

        useEffect(() => {
            treeStore.setMaxSelectedBranchCount(
                props.maxSelectedHierarchyCount,
            );
        }, [props.maxSelectedHierarchyCount, treeStore]);
        const resolvedAnchorWidth =
            anchorEl && typeof anchorEl !== "function"
                ? anchorEl.clientWidth
                : undefined;
        const inputPopperWidth =
            popperWidth ?? props.width ?? resolvedAnchorWidth;
        const inputPopperPlacement =
            props.popperPlacement ?? "bottom-start";
        const inputPopperModifiers = [
            {
                name: "offset",
                options: {
                    offset: [0, 8],
                },
            },
            {
                name: "flip",
                options: {
                    fallbackPlacements: [
                        "top-start",
                        "bottom-end",
                        "top-end",
                        "right-start",
                        "left-start",
                    ],
                },
            },
            {
                name: "preventOverflow",
                options: {
                    altAxis: true,
                    padding: 8,
                },
            },
        ];

        const handleInputClick = (event: React.MouseEvent<HTMLElement>) => {
            if (props.viewOnly) return; // prevent opening when view-only (e.g., pinned dashboard)
            if (!props.fixedPopper) {
                setAnchorEl(
                    (inputRef.current as HTMLElement | null) ??
                        event.currentTarget,
                );
            }
            setPopperOpen(!popperOpen);
        };

        const handlePopperClose = (event: MouseEvent | TouchEvent) => {
            setPopperOpen(false);
            treeStore.setOpenedBranchIds([]);
        };

        const handleDialogClose = () => {
            setDialogOpen(false);
            treeStore.setOpenedBranchIds([]);
        };

        const handleDialogOpen = () => {
            setDialogOpen(true);
        };

        const hierCountLabel =
            treeStore.selectedBranchIds.length === 0
                ? props.placeholder ?? "Service Hierarchies"
                : treeStore.selectedBranchIds.length === 1
                ? treeStore.selectedBranchIds.length + " Hierarchy Selected"
                : treeStore.selectedBranchIds.length + " Hierarchies Selected";

        // load top level hierarchies and clear old data
        useEffect(() => {
            if (props.orgId && props.userId) {
                treeStore.setOrganizationId(props.orgId);
                treeStore.setIsTreeLoading(true);

                async function fetchData() {
                    await loadUserServiceHierarchiesHelper(
                        hierService,
                        treeStore,
                        availableHierarchyIds,
                    );
                    treeStore.setIsTreeLoading(false);
                }
                fetchData();
                // when orgId or userId changes we need to reset hierarchies
                treeStore.selectedBranchLabels = [];
            }
        }, [
            props.orgId,
            props.userId,
            availableHierarchyIds,
            normalizedAvailableHierarchyIdsKey,
            hierService,
            treeStore,
        ]);

        // Ensures we set the local treeStore's categories if init values from Application Filter change
        useEffect(() => {
            treeStore.selectedCategories =
                props.initCategories?.categories ?? [];
            treeStore.selectedSubcategories =
                props.initCategories?.subcategories ?? [];
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            props.initCategories?.categories,
            props.initCategories?.subcategories,
        ]);

        // Ensures we update Application Filter Stores with Categories
        useEffect(() => {
            props.storeFilterCategories?.(treeStore.selectedCategories);
            props.storeFilterSubategories?.(treeStore.selectedSubcategories);

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [treeStore.selectedCategories, treeStore.selectedSubcategories]);

        // set initial hierarchy data, used for preserving hierarchies from a store when component unmounts
        useEffect(() => {
            if (props.initSelectedHierarchyIds) {
                const selectedLabelById = Object.fromEntries(
                    (props.initSelectedHierarchyIds ?? []).map(
                        (hierarchyId, index) => [
                            hierarchyId,
                            props.initSelectedHierarchyLabels?.[index],
                        ],
                    ),
                );
                const nextHierarchyIds = hasAvailableHierarchyRestriction
                    ? props.initSelectedHierarchyIds.filter((hierarchyId) =>
                          availableHierarchyIdSet.has(`${hierarchyId}`),
                      )
                    : props.initSelectedHierarchyIds;
                const cappedHierarchyIds = props.maxSelectedHierarchyCount
                    ? nextHierarchyIds.slice(0, props.maxSelectedHierarchyCount)
                    : nextHierarchyIds;
                treeStore.selectedBranchIds = cappedHierarchyIds;
                treeStore.selectedBranchLabels = cappedHierarchyIds.map(
                    (hierarchyId) => selectedLabelById[hierarchyId] ?? hierarchyId,
                );
            } else {
                treeStore.selectedBranchIds = [];
                treeStore.selectedBranchLabels = [];
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            treeStore,
            props.initSelectedHierarchyIds,
            props.initSelectedHierarchyLabels,
            props.displayType,
            hasAvailableHierarchyRestriction,
            availableHierarchyIdSet,
            props.maxSelectedHierarchyCount,
        ]);

        // set onChange handler
        useEffect(() => {
            treeStore.onSelectedBranchIdsChange = (
                selectedBranchIds: string[],
            ) => {
                const nextHierarchyIds = hasAvailableHierarchyRestriction
                    ? selectedBranchIds.filter((hierarchyId) =>
                          availableHierarchyIdSet.has(`${hierarchyId}`),
                      )
                    : selectedBranchIds;
                const cappedHierarchyIds = props.maxSelectedHierarchyCount
                    ? nextHierarchyIds.slice(0, props.maxSelectedHierarchyCount)
                    : nextHierarchyIds;

                if (cappedHierarchyIds.length !== selectedBranchIds.length) {
                    treeStore.selectedBranchIds = cappedHierarchyIds;
                    treeStore.selectedBranchLabels =
                        treeStore.selectedBranchLabels.slice(
                            0,
                            cappedHierarchyIds.length,
                        );
                    return;
                }

                props.setHierarchyIds(cappedHierarchyIds);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            availableHierarchyIdSet,
            hasAvailableHierarchyRestriction,
            props.maxSelectedHierarchyCount,
            props.setHierarchyIds,
            treeStore.selectedBranchIds,
        ]);

        // domain: analyticx > eddy dash > filter
        const hierBaseOnly = (
            <HierarchySelectorBase
                fullHeight
                popperWidth={popperWidth}
                {...props}
                availableHierarchyIds={availableHierarchyIds}
                treeStore={treeStore}
                onApplyClick={() => {
                    // apply the hierarchies, I don't believe this changes. What I need to change is what gets selected
                    // when cat / sub-cat are selected/unselected
                    props.onSaveUpdateWithHierarchies(
                        treeStore.selectedBranchIds,
                        treeStore,
                    );
                    props.displayType === "inputDialog" && handleDialogClose();

                    props.displayType === "input" && setPopperOpen(false);
                    // for reclassify dialog to run check only on apply
                    if (location.pathname === "/app/classifiers") {
                        rbcReclassifyStore.setHierarchiesApplied();
                    }
                }}
            />
        );

        // externally controlled popper for dashboard & widgets
        const popperOnly: React.ReactNode = (
            <ClickAwayListener
                onClickAway={
                    props.handlePopperClose ? props.handlePopperClose : () => {}
                }
            >
                <Popper
                    id={"hierarchies-popper"}
                    open={!!props.popperOpen}
                    anchorEl={props.anchorEl}
                    sx={() => {
                        return props.customZIndex
                            ? {
                                  borderRadius: "8px",
                                  zIndex: props.customZIndex,
                                  "@media (max-height: 454px)": {
                                      top: "60px !important",
                                  },
                              }
                            : { borderRadius: "8px" };
                    }}
                    placement={props.popToLeft ? "left" : "bottom"}
                    style={props.popToLeft ? { marginRight: "20px" } : {}}
                >
                    {hierBaseOnly}
                </Popper>
            </ClickAwayListener>
        );

        // domain: conversations > search filters && workflows
        const hierInput = (
            <>
                <Grid
                    id="filters-hierarchy-input"
                    container
                    alignItems="center"
                    display="flex"
                    flexDirection="row"
                    flexWrap="nowrap"
                    minHeight={props.removeMinHeight ? "auto" : "52px"}
                >
                    <Grid
                        item
                        ref={inputRef}
                        sx={{
                            width: props.width ? props.width : "100%",
                            minWidth: props.removeMinWidth ? "auto" : "350px",
                        }}
                    >
                        <AcxMainTextField
                            id={"hierarchy-selector-input"}
                            fullWidth
                            containerStyle={{
                                width: "100%",
                                cursor: props.viewOnly ? "default" : "pointer",
                            }}
                            aria-describedby={popperId}
                            placeholderText={
                                props.placeholder ?? "Service Hierarchies"
                            }
                            onClick={handleInputClick}
                            inputProps={{
                                style: {
                                    cursor: props.viewOnly
                                        ? "default"
                                        : "pointer",
                                },
                            }}
                            readonly
                            value={hierCountLabel}
                            sx={{
                                cursor: props.viewOnly ? "default" : "pointer",
                            }}
                            endAdornment={
                                props.viewOnly &&
                                props.initSelectedHierarchyIds?.length ===
                                    0 ? null : props.viewOnly ? (
                                    <Typography
                                        sx={(theme) => ({
                                            marginRight: "6px",
                                            cursor: "default",
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                        })}
                                    >
                                        View
                                    </Typography>
                                ) : (
                                    <DownChevronSvg
                                        style={{
                                            marginRight: "6px",
                                            cursor: "pointer",
                                        }}
                                    />
                                )
                            }
                            labelText={props.labelText ?? ""}
                        />
                    </Grid>
                </Grid>
                {popperOpen && (
                    <ClickAwayListener onClickAway={handlePopperClose}>
                        <Popper
                            id={popperId}
                            open={popperOpen}
                            anchorEl={anchorEl}
                            modifiers={inputPopperModifiers}
                            sx={() => {
                                const popperStyles: SxProps<Theme> = {
                                    borderRadius: "8px",
                                    maxWidth: "calc(100vw - 16px)",
                                    width: inputPopperWidth,
                                };

                                if (props.customZIndex) {
                                    popperStyles.zIndex = props.customZIndex;
                                }

                                return popperStyles;
                            }}
                            placement={inputPopperPlacement}
                        >
                            {hierBaseOnly}
                        </Popper>
                    </ClickAwayListener>
                )}
            </>
        );

        // domain: analytics > builder reclassify
        const hierInputDialog = (
            <>
                <Grid
                    container
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "flex-end",
                        minHeight: "52px",
                    }}
                >
                    <Grid
                        item
                        xs={12}
                        onClick={handleDialogOpen}
                        sx={{
                            minWidth: "100%",
                            maxWidth: "450px",
                            padding: "0px",
                        }}
                    >
                        <AcxSelectMulti
                            id={"hierarchy-input-dialog"}
                            inputLabel={
                                props.labelText ?? "Select Hierarchy(ies)"
                            }
                            fullWidth
                            placeholder={
                                props.placeholder ?? "Select hierarchy(ies)"
                            }
                            defaultValue={
                                treeStore.selectedBranchLabels?.map(
                                    (branchLabel) => ({
                                        label: (
                                            <Typography
                                                sx={{
                                                    color: "inherit",
                                                    fontWeight: "inherit",
                                                    width: "100%",
                                                    fontSize: "inherit",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {branchLabel}
                                            </Typography>
                                        ),
                                        value: treeStore.selectedBranchLabels[
                                            branchLabel
                                        ],
                                    }),
                                ) ?? undefined
                            }
                            options={[]}
                            valueField="value"
                            labelField="label"
                            containerHeight="auto"
                            maxContainerHeight="50px"
                            alignControls="flex-start"
                        />
                    </Grid>
                </Grid>
                <Dialog
                    open={dialogOpen}
                    aria-labelledby="hier-dialog-title"
                    aria-describedby="hier-dialog-description"
                    maxWidth={"md"}
                    sx={{ padding: "0px", zIndex: 2000 }}
                >
                    <Grid
                        container
                        item
                        xs={12}
                        justifyContent="flex-end"
                        sx={{ width: "100%" }}
                    >
                        <IconButton
                            aria-label="close"
                            sx={{
                                color: theme.palette.black.main,
                                position: "relative",
                                top: "11px",
                                right: "11px",
                                marginBottom: "5px",
                                padding: "0px",
                            }}
                            onClick={handleDialogClose}
                            size="large"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    {hierBaseOnly}
                </Dialog>
            </>
        );

        const renderHierarchySelectorUI = () => {
            switch (props.displayType) {
                case "baseOnly":
                    return hierBaseOnly;
                case "input":
                    return hierInput;
                case "inputDialog":
                    return hierInputDialog;
                case "popperOnly":
                    return popperOnly;
                default:
                    return null;
            }
        };

        return renderHierarchySelectorUI();
    },
);

export const loadUserServiceHierarchiesHelper = async (
    hierService: HierarchyService,
    treeStore: RecursiveTreeStore,
    availableHierarchyIds?: string[],
) => {
    treeStore.setIsTreeLoading(true);
    const validHierarchyIds = (availableHierarchyIds ?? []).filter(
        (hierarchyId) => !!hierarchyId,
    );

    const userTopLevelHierarchiesResponse =
        await hierService.getTopLevelHierarchiesForUser(
            undefined,
            validHierarchyIds.length > 0 ? validHierarchyIds : undefined,
        );

    const userTopLevelTiers =
        userTopLevelHierarchiesResponse.childrenHierarchies;

    // reset openedBranchIds to ensure state is properly maintained when resetting topLevel
    treeStore.openedBranchIds = [];
    treeStore.setTopLevelBranches(userTopLevelTiers);
    treeStore.setIsTreeLoading(false);
};

export default AcxHierarchySelector;
